import type { ParseKeys } from 'i18next'

import type { Notification } from '@/types/domain'

/* The bell's server-built titles the client can say in the reader's language
 * (QW-1, L10).
 *
 * THE FALLBACK since story B5. A row published since then carries a message
 * code and its params, and `notificationMessages.ts` words the whole row from
 * them; this table is consulted only for a row that cannot be worded that way
 * — one written before codes existed (`code: ""`), or a code this build does
 * not know yet.
 *
 * The outbox composes each title in English at publish time (AD-27 leaves the
 * reader's language to the client, and the producers say so beside each
 * string). A title is a server-built SENTENCE rather than a code, so it is
 * translated only while it is exactly a sentence this table knows, under the
 * topic that produces it: a reworded title, or the same words under another
 * topic, renders as the server sent it. Matching both is what keeps a stale
 * row or a coincidence from being shown as a different event.
 *
 * Only rows whose DETAIL is data or empty are here, so a translated row reads
 * wholly in Russian. Titles whose detail is itself an English sentence —
 * "Rate change rejected" ("… refused; the rate stays …"), the quote send,
 * conversion, participant and answer notices, `case.status`, chat — stay in
 * English until the server sends parameters the client can word.
 *
 * Translated at RENDER, never in `notificationFromWire`: the feed cache outlives
 * a language switch, and a title translated on the way in would keep the
 * language it arrived in. `notificationTitles.test.ts` reads the Go producers
 * and fails when a title or a topic here is not what the server writes. */
export const NOTIFICATION_TITLES: readonly {
  topic: string
  en: string
  key: ParseKeys
}[] = [
  {
    topic: 'quote.team-assigned',
    en: 'You were added to this quote',
    key: 'common.notifications.titles.teamAssigned',
  },
  {
    topic: 'quote.pricing-requested',
    en: 'A price is wanted on this quote',
    key: 'common.notifications.titles.pricingRequested',
  },
  /* The price request's answer and its return (S-2a). Both carry a code from
     the day they ship, so these rows are reached only by a build that lags the
     server's registry; they are here so that build still says them in Russian.
     Composed in quote/pricing.go, not team.go. */
  {
    topic: 'quote.pricing-ready',
    en: 'The price is ready on this quote',
    key: 'common.notifications.titles.pricingReady',
  },
  {
    topic: 'quote.pricing-reopened',
    en: 'This quote changed and needs pricing again',
    key: 'common.notifications.titles.pricingReopened',
  },
  {
    topic: 'case.assigned',
    en: 'You were assigned to this case',
    key: 'common.notifications.titles.caseAssigned',
  },
  {
    topic: 'case.status-sent',
    en: 'Status sent to client',
    key: 'common.notifications.titles.statusSent',
  },
  {
    topic: 'finance.rate-change-requested',
    en: 'Rate change requested',
    key: 'common.notifications.titles.rateChangeRequested',
  },
  {
    topic: 'finance.rate-change-decided',
    en: 'Rate change approved',
    key: 'common.notifications.titles.rateChangeApproved',
  },
  {
    topic: 'case.expense-changed',
    en: 'Expense line recorded',
    key: 'common.notifications.titles.expenseRecorded',
  },
  {
    topic: 'case.expense-changed',
    en: 'Expense line amended',
    key: 'common.notifications.titles.expenseAmended',
  },
  {
    topic: 'case.expense-changed',
    en: 'Expense line removed',
    key: 'common.notifications.titles.expenseRemoved',
  },
  {
    topic: 'finance.payment-awaits-confirmation',
    en: 'Payment awaits confirmation',
    key: 'common.notifications.titles.paymentAwaitsConfirmation',
  },
]

/** Titles published under a topic the table above covers that stay in English
 *  ON PURPOSE, because their detail is itself an English sentence and a
 *  Russian title over it would make the row half-translated.
 *
 *  Listed rather than merely absent so that a NEW title under one of these
 *  topics cannot slip through untranslated unnoticed: `notificationTitles.test.ts`
 *  collects every title the Go producers publish under the table's topics and
 *  requires each to be a row above or an entry here. */
export const UNTRANSLATED_TITLES: readonly { topic: string; en: string }[] = [
  // Detail: "<requested> refused; the rate stays <current>".
  { topic: 'finance.rate-change-decided', en: 'Rate change rejected' },
]

/** The catalogue key for one bell row's title, or undefined when its topic and
 *  its text are not BOTH a pair this table holds — the caller then prints the
 *  stored title. */
export function notificationTitleKey(
  item: Pick<Notification, 'topic' | 'title'>,
): ParseKeys | undefined {
  return NOTIFICATION_TITLES.find((row) => row.topic === item.topic && row.en === item.title)
    ?.key
}
